import moment from 'moment-timezone';
import { Transaction } from '../../../../ProfitCalculations/types';
import {
  formatBalance,
  formatNumber,
  formatPercentage,
  formatPrice,
} from '../../../../../helpers/NumberFormatter';
import { customTransactionKind } from '../../../../../helpers/customTransactions';

export const fmtPrice = (value: number) =>
  formatPrice(value, true, 2, true, 'never');

export const fmtBalance = (value: number) =>
  formatBalance(value, true, value >= 1000 ? 0 : 2);

export const fmtPct = (value: number, signed = false) =>
  // Default sign mode is 'auto' (undefined) — a minus for losses, no plus for
  // gains. 'never' would strip the minus too, making a loss look like a gain.
  formatPercentage(value, 2, false, signed ? 'always' : undefined);

export const fmtQty = (value: number) => formatNumber(value, 6);

export const fmtDate = (timestamp: number) =>
  moment(timestamp).utc().format('DD MMM YYYY HH:mm');

export const fmtDateShort = (timestamp: number) =>
  moment(timestamp).utc().format('DD MMM YYYY');

export const fmtTimespan = (ms: number): string => {
  const duration = moment.duration(ms);
  const years = Math.floor(duration.asYears());
  const months = Math.floor(duration.subtract(years, 'years').asMonths());
  const days = Math.floor(duration.subtract(months, 'months').asDays());
  const parts: string[] = [];
  if (years) parts.push(`${years} year${years === 1 ? '' : 's'}`);
  if (months) parts.push(`${months} month${months === 1 ? '' : 's'}`);
  if (days) parts.push(`${days} day${days === 1 ? '' : 's'}`);
  return parts.length ? parts.join(', ') : '0 days';
};

export const fmtTimespanShort = (ms: number): string => {
  const duration = moment.duration(ms);
  const years = Math.floor(duration.asYears());
  const months = Math.floor(duration.subtract(years, 'years').asMonths());
  const parts: string[] = [];
  if (years) parts.push(`${years}y`);
  if (months || !years) parts.push(`${months}m`);
  return parts.join(' ');
};

export const fmtMultiplier = (value: number) => `×${value.toFixed(2)}`;

export const fmtCompact = (value: number): string => {
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}$${Math.round(abs / 1_000_000)}M`;
  if (abs >= 1_000) return `${sign}$${Math.round(abs / 1_000)}K`;
  return `${sign}$${Math.round(abs)}`;
};

/**
 * How many times more stable value the bot produced versus simply buying the
 * asset at the start and holding to the end. Buy & hold end value =
 * startBalance scaled by the asset's price change over the full period.
 */
export const vsBuyAndHold = (
  startBalanceUsdt: number,
  endBalanceUsdt: number,
  firstPrice: number,
  lastPrice: number,
): number => {
  if (startBalanceUsdt <= 0 || firstPrice <= 0) return 0;
  const buyAndHoldEnd = startBalanceUsdt * (lastPrice / firstPrice);
  if (buyAndHoldEnd <= 0) return 0;
  return endBalanceUsdt / buyAndHoldEnd;
};

export const isCore = (transactionType: string): boolean =>
  !transactionType.includes('TAKE_PROFIT') &&
  !transactionType.includes('STOP_LOSS');

export const isStopLoss = (transactionType: string): boolean =>
  transactionType.includes('STOP_LOSS');

export const isTakeProfit = (transactionType: string): boolean =>
  transactionType.includes('TAKE_PROFIT') && !isStopLoss(transactionType);

/**
 * CustomTransactions predicates — all delegate to `helpers/customTransactions`, the single
 * classifier, so a new row type is recognised everywhere at once instead of by whichever
 * substring each call site happened to test for.
 */

/** A CPC balance-distribution sync. */
export const isDistribution = (transactionType: string): boolean =>
  customTransactionKind(transactionType) === 'DISTRIBUTION';

/** Earn yield credited to the subscription. Its own category because it lands DAILY, so it
 * gets an opt-in filter of its own rather than riding along with deposits. */
export const isEarnInterest = (transactionType: string): boolean =>
  customTransactionKind(transactionType) === 'INTEREST';

/** A deposit, a withdrawal or a correction — the balance moved without an order. */
export const isBalanceChange = (transactionType: string): boolean => {
  const kind = customTransactionKind(transactionType);
  return kind === 'DEPOSIT' || kind === 'WITHDRAWAL' || kind === 'CORRECTION';
};

/** Any CustomTransactions row — never present on an SPC result. */
export const isCustomTransactionType = (transactionType: string): boolean =>
  customTransactionKind(transactionType) !== null;

/**
 * Which side a transaction's profit is realised in — purely a function of
 * orderType. A BUY (core, take-profit or stop-loss) realises into the asset;
 * a SELL realises into stable. Every transaction populates exactly the
 * matching field: profitInAsset for a BUY, profitInUsdt for a SELL.
 */
export const txProfitSide = (tx: Transaction): 'stable' | 'asset' =>
  tx.orderType === 'BUY' ? 'asset' : 'stable';

export const txProfitValue = (tx: Transaction): number =>
  tx.orderType === 'BUY' ? tx.profitInAsset : tx.profitInUsdt;

/** Fraction (0..1) of a transaction's total balance value held in stable. */
export const txStableFraction = (tx: Transaction): number => {
  const assetValue = tx.balanceInAsset * tx.orderPrice;
  const total = tx.balanceInUsdt + assetValue;
  if (total <= 0) return 0;
  return tx.balanceInUsdt / total;
};
